import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  FileDownloadOutlined,
  InfoOutlined,
  OpenInFull,
  TrendingUpOutlined,
  WarningAmberOutlined,
  TrendingDownOutlined,
} from '@mui/icons-material';

const INSIGHT_STYLES = {
  green:  { bg: '#F0FDF4', color: '#166534', accent: '#66BB6A', Icon: TrendingUpOutlined },
  success: { bg: '#F0FDF4', color: '#166534', accent: '#22C55E', Icon: CheckCircleOutline },
  blue:   { bg: '#EFF6FF', color: '#1D4ED8', accent: '#42A5F5', Icon: InfoOutlined },
  yellow: { bg: '#FFFBEB', color: '#92400E', accent: '#D6B46D', Icon: WarningAmberOutlined },
  red:    { bg: '#FEF2F2', color: '#991B1B', accent: '#E57373', Icon: TrendingDownOutlined },
};

/**
 * Standardized header block for all report chart cards.
 * Export and enlarge buttons appear only when the parent card is hovered.
 */
const ReportCardHeader = ({ title, insight, handleExport, handleEnlarge, showButton = true, trailing }) => {
  return (
    <Box sx={{ flexShrink: 0, mb: '2px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.75}>
        <Box display="flex" alignItems="center" gap={0.5} minWidth={0} flex="1 1 auto">
          <Typography
            variant="h6"
            sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.3, color: '#1F2937' }}
            noWrap
            title={title}>
            {title}
          </Typography>
          {insight && (
            <Tooltip
              title={insight}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#f1f5f9',
                    backgroundColor: 'rgba(15, 23, 42, 0.92)',
                    maxWidth: 260,
                    px: 1.25,
                    py: 0.6,
                    borderRadius: '6px',
                  },
                },
                arrow: {
                  sx: { color: 'rgba(15, 23, 42, 0.92)' },
                },
              }}>
              <InfoOutlined
                sx={{
                  fontSize: 14,
                  color: '#9CA3AF',
                  cursor: 'pointer',
                  flexShrink: 0,
                  '&:hover': { color: '#6B7280' },
                }}
              />
            </Tooltip>
          )}
          {trailing}
        </Box>
        {((showButton && handleExport) || handleEnlarge) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            {handleEnlarge && (
              <Tooltip title="Enlarge" arrow placement="top">
                <IconButton
                  size="small"
                  className="report-chart-header-action"
                  onClick={() => handleEnlarge()}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    color: '#9CA3AF',
                    p: 0.4,
                    '&:hover': {
                      color: '#475569',
                      bgcolor: '#f1f5f9',
                    },
                  }}>
                  <OpenInFull sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {showButton && handleExport && (
              <Tooltip title="Export" arrow placement="top">
                <IconButton
                  size="small"
                  className="report-chart-header-action"
                  onClick={() => handleExport()}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    color: '#9CA3AF',
                    p: 0.4,
                    '&:hover': {
                      color: '#475569',
                      bgcolor: '#f1f5f9',
                    },
                  }}>
                  <FileDownloadOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const ReportInsightBanner = ({ text, style, centered = false }) => {
  if (!text) return null;
  const cfg = INSIGHT_STYLES[style] || INSIGHT_STYLES.blue;
  const { Icon } = cfg;
  return (
    <Box
      sx={{
        mt: 0.5,
        mb: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: centered ? 'center' : 'flex-start',
        gap: 0.6,
        backgroundColor: cfg.bg,
        borderLeft: centered ? 'none' : `3px solid ${cfg.accent}`,
        border: centered ? `1px solid ${cfg.accent}33` : undefined,
        borderRadius: '6px',
        px: centered ? 1.25 : 1,
        py: 0.5,
      }}>
      <Icon sx={{ fontSize: 13, color: cfg.accent, flexShrink: 0, opacity: 0.8 }} />
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 500,
          color: cfg.color,
          lineHeight: 1.4,
          textTransform: 'none',
          textAlign: centered ? 'center' : 'left',
        }}>
        {text}
      </Typography>
    </Box>
  );
};

export default ReportCardHeader;
